import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Divider,
  Form,
  InputNumber,
  DatePicker,
  Radio,
  Button,
  Table,
  Row,
  Col,
  Space,
  Alert,
  Descriptions,
  Tag,
  message,
} from "antd";
import { CalculatorOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import axios from "axios";

// ตัวอย่าง ตารางดิว พิพากษา ปกติ — preview อย่างเดียว ไม่บันทึก DB (phase 1)
const URL =
  "https://api-asset-investigation-1024031113167.asia-southeast1.run.app/api-new/preview-calculate-due-judge";
const headers = {
  "Content-Type": "application/json",
  Authorization: "Basic dXNlcnMtbGF3eWVyOm9uZWxlYXNpbmc=",
};

const money = (val) =>
  val != null
    ? Number(val).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "-";

// detail ของ v3 มี object ซ้อน (interest_cap, neg_amort_clamp) → แสดงเป็น key : value
const renderDetail = (v) => {
  if (v && typeof v === "object") {
    return (
      <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }}>
        {Object.entries(v).map(([k, x]) => (
          <Descriptions.Item key={k} label={k}>
            {renderDetail(x)}
          </Descriptions.Item>
        ))}
      </Descriptions>
    );
  }
  if (typeof v === "number")
    return v.toLocaleString(undefined, { maximumFractionDigits: 6 });
  return String(v);
};

const columns = [
  { title: "งวดที่", dataIndex: "NOPAY", align: "center" },
  {
    title: "ดิวก่อนหน้า",
    dataIndex: "LASTDUEDT",
    align: "center",
    render: (t) => (t ? dayjs(t).format("DD/MM/YYYY") : "-"),
  },
  {
    title: "วันดิว",
    dataIndex: "DUEDATE",
    align: "center",
    render: (t) => (t ? dayjs(t).format("DD/MM/YYYY") : "-"),
  },
  { title: "จำนวนวัน", dataIndex: "DELAYDAY", align: "center" },
  { title: "ค่างวด", dataIndex: "DUEAMT", align: "right", render: money },
  { title: "ดอกเบี้ย", dataIndex: "DUEINTEFF", align: "right", render: money },
  { title: "เงินต้น", dataIndex: "DUETONEFF", align: "right", render: money },
  {
    title: "เงินต้นคงเหลือ",
    dataIndex: "TONBALANCE",
    align: "right",
    render: money,
  },
];

export default function DueTableJudgmentTab({ judgeData }) {
  const [form] = Form.useForm();

  // ค่าเริ่มต้นจากข้อมูลสัญญา (judge[0] + loanJudge)
  const defaults = useMemo(() => {
    const judge = judgeData?.judge?.[0];
    const loanJudge = judgeData?.loanJudge;
    const num = (v) => (v != null && v !== "" ? Number(v) : undefined);
    return {
      calculate: 1,
      idJudge: judge?.idJudge,
      NCSHPRC: num(loanJudge?.principalRemain),
      FDATE: judge?.FDATE ? dayjs(judge.FDATE) : undefined,
      T_NOPAY: num(loanJudge?.T_NOPAY) ?? 24,
      TOT_UPAY: num(loanJudge?.TOT_UPAY),
      annualInterestRate: num(judge?.interestRate),
    };
  }, [judgeData]);

  useEffect(() => {
    form.setFieldsValue(defaults);
  }, [defaults, form]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const calculate = Form.useWatch("calculate", form);

  const onFinish = async (values) => {
    if (!values.idJudge && !values.NCSHPRC) {
      message.warning("กรุณาระบุ idJudge หรือ เงินต้น อย่างใดอย่างหนึ่ง");
      return;
    }
    const payload = {
      calculate: values.calculate,
      FDATE: values.FDATE.format("YYYY-MM-DD"),
      T_NOPAY: values.T_NOPAY,
    };
    if (values.idJudge) payload.idJudge = values.idJudge;
    if (values.NCSHPRC) payload.NCSHPRC = values.NCSHPRC;
    if (values.calculate !== 3)
      payload.annualInterestRate = values.annualInterestRate;
    if (values.calculate !== 2) payload.TOT_UPAY = values.TOT_UPAY;

    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(URL, payload, { headers });
      setResult(res.data);
    } catch (err) {
      console.error(err);
      setResult(null);
      setError(err.response?.data || { message: "เกิดข้อผิดพลาดในการคำนวณ" });
    }
    setLoading(false);
  };

  const applySuggest = () => {
    const s = result?.suggest;
    if (!s) return;
    form.setFieldsValue({
      T_NOPAY: s.T_NOPAY,
      TOT_UPAY: s.TOT_UPAY,
      ...(s.annualInterestRate != null && {
        annualInterestRate: s.annualInterestRate,
      }),
    });
  };

  const summary = result?.summary;
  const detail = result?.detail;
  return (
    <Card>
      <Divider orientation={"center"}>
        <b>
          <u>ตัวอย่าง ตารางดิว พิพากษา ปกติ</u>
        </b>
      </Divider>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={defaults}
      >
        <Form.Item label="แบบการคำนวณ" name="calculate">
          <Radio.Group buttonStyle="solid" onChange={() => setResult(null)}>
            <Radio.Button value={1}>v1 ลดต้นลดดอก (รายวัน)</Radio.Button>
            <Radio.Button value={2}>v2 Flat Rate</Radio.Button>
            <Radio.Button value={3}>v3 IRR (เพดานดอก)</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="idJudge"
              name="idJudge"
              extra="ส่งอย่างใดอย่างหนึ่งกับเงินต้น"
            >
              <InputNumber style={{ width: "100%" }} min={1} precision={0} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="เงินต้น (NCSHPRC)"
              name="NCSHPRC"
              extra="ใส่แล้วจะใช้แทนยอดจาก idJudge"
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                precision={2}
                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(v) => v.replace(/,/g, "")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="วันดิวงวดแรก (FDATE)"
              name="FDATE"
              rules={[{ required: true, message: "กรุณาเลือกวันดิวงวดแรก" }]}
            >
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="จำนวนงวด (T_NOPAY)"
              name="T_NOPAY"
              rules={[{ required: true, message: "กรุณากรอกจำนวนงวด" }]}
            >
              <InputNumber style={{ width: "100%" }} min={1} precision={0} />
            </Form.Item>
          </Col>
          {calculate !== 3 && (
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                label="ดอกเบี้ย %/ปี (annualInterestRate)"
                name="annualInterestRate"
                rules={[
                  {
                    required: true,
                    message: "กรุณากรอกดอกเบี้ย (0 = ไม่คิดดอก)",
                  },
                ]}
                extra={
                  calculate === 2
                    ? "Flat: ตัวเลขต่อเดือนจากหน้าจอรถ ต้อง ×12 ก่อน (1.1 → 13.2)"
                    : "0 = ไม่คิดดอก"
                }
              >
                <InputNumber style={{ width: "100%" }} min={0} step={0.1} />
              </Form.Item>
            </Col>
          )}
          {calculate !== 2 && (
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                label="ค่างวด (TOT_UPAY)"
                name="TOT_UPAY"
                rules={[{ required: true, message: "กรุณากรอกค่างวด" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0.01}
                  precision={2}
                  formatter={(v) =>
                    `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(v) => v.replace(/,/g, "")}
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        <Row justify="end">
          <Space>
            <Button
              onClick={() => {
                form.resetFields();
                setResult(null);
                setError(null);
              }}
            >
              ล้างค่า
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              icon={<CalculatorOutlined />}
              loading={loading}
            >
              คำนวณ
            </Button>
          </Space>
        </Row>
      </Form>

      {error && (
        <Alert
          style={{ marginTop: 16 }}
          type="error"
          showIcon
          message={error.message || "คำนวณไม่สำเร็จ"}
          description={
            error.remainJudge != null && (
              <>
                ยอดพิพากษา {money(error.total_jugde)} · จ่ายแล้ว{" "}
                {money(error.paid)} · <b>คงเหลือ {money(error.remainJudge)}</b>{" "}
                — เงินต้นต้องไม่เกินยอดคงเหลือ
              </>
            )
          }
        />
      )}

      {result && (
        <>
          <Divider />
          {result.warnings?.length > 0 && (
            <Alert
              style={{ marginBottom: 16 }}
              type={result.valid ? "warning" : "error"}
              showIcon
              message={result.valid ? "คำเตือน" : "ตารางใช้ไม่ได้"}
              description={
                <ul style={{ margin: 0 }}>
                  {result.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              }
            />
          )}

          <Descriptions
            bordered
            size="small"
            column={{ xs: 1, sm: 2, md: 3 }}
            title={
              <Space>
                สรุปผล v{result.calculate}{" "}
                {result.valid ? (
                  <Tag color="green">ใช้ได้</Tag>
                ) : (
                  <Tag color="red">ใช้ไม่ได้</Tag>
                )}
              </Space>
            }
          >
            <Descriptions.Item label="เงินต้น">
              {money(summary?.principal)}
            </Descriptions.Item>
            <Descriptions.Item label="รวมยอดชำระ">
              {money(summary?.totalPay)}
            </Descriptions.Item>
            <Descriptions.Item label="รวมดอกเบี้ย">
              {money(summary?.totalInterest)}
            </Descriptions.Item>
            <Descriptions.Item label="งวดสุดท้าย">
              {money(summary?.lastDueAmt)}
            </Descriptions.Item>
            <Descriptions.Item label="ดอกเบี้ยแท้จริง (%/ปี)">
              <b style={{ color: "red" }}>
                {summary?.effectiveAnnualRate
                  ? Number(summary.effectiveAnnualRate).toFixed(2) + "%"
                  : "-"}
              </b>
            </Descriptions.Item>
            {/* ซ่อน interest_cap, neg_amort_clamp ไว้ก่อน */}
            {detail &&
              Object.entries(detail)
                .filter(
                  ([k]) => !["interest_cap", "neg_amort_clamp"].includes(k),
                )
                .map(([k, v]) => (
                  <Descriptions.Item
                    key={k}
                    label={k}
                    span={v && typeof v === "object" ? 3 : 1}
                  >
                    {renderDetail(v)}
                  </Descriptions.Item>
                ))}
          </Descriptions>

          {result.suggest && (
            <Alert
              style={{ marginTop: 16 }}
              type="info"
              showIcon
              message={
                <>
                  ค่าแนะนำ: งวด {result.suggest.T_NOPAY} · ค่างวด{" "}
                  {money(result.suggest.TOT_UPAY)}
                  {result.suggest.annualInterestRate != null && (
                    <>
                      {" "}
                      · ดอก {result.suggest.annualInterestRate}%/ปี (product{" "}
                      {result.suggest.product})
                    </>
                  )}
                </>
              }
              action={
                <Button size="small" onClick={applySuggest}>
                  ใช้ค่าแนะนำ
                </Button>
              }
            />
          )}

          <Table
            style={{ marginTop: 16 }}
            dataSource={result.rows || []}
            columns={columns}
            rowKey="NOPAY"
            bordered
            size="small"
            scroll={{ x: "max-content" }}
            pagination={{ pageSize: 12 }}
            onRow={(r) => ({
              style:
                r.DUEINTEFF < 0 ||
                r.DUETONEFF < 0 ||
                r.TONBALANCE < 0 ||
                r.DUEAMT < 0
                  ? { background: "#fff1f0" }
                  : {},
            })}
          />
        </>
      )}
    </Card>
  );
}
